import type { Campaign } from '../campaignsApi';
import type { GoogleIntelligenceRecord, GooglePerformanceItem } from '../googleIntelligence';
import './campaign-journey.css';
import './google-campaign-results.css';

function number(value: number) { return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 }); }

function money(micros: number | null, currency: string | null) {
  if (micros === null) return '—';
  return currency ? (micros / 1e6).toLocaleString('pt-BR', { style: 'currency', currency }) : number(micros / 1e6);
}

function PerformanceTable({ items, label, currency }: { items: GooglePerformanceItem[]; label: string; currency: string | null }) {
  if (!items.length) return <p>Ainda não há dados de {label.toLowerCase()} nesta janela.</p>;
  return <div className="google-performance-table"><table><caption>{label}</caption><thead><tr>
    <th>Anúncio / Palavra-chave</th><th>Cliques</th><th>Conversões</th><th>Custo</th><th>CPA</th><th>Aprendizado</th>
  </tr></thead><tbody>{items.map(item => <tr key={item.id}>
    <td>{item.text || item.headlines?.join(' · ') || `Anúncio ${item.id}`}</td><td>{number(item.clicks)}</td>
    <td>{number(item.conversions)}</td><td>{money(item.costMicros, currency)}</td><td>{money(item.cpaMicros, currency)}</td>
    <td title={item.rankReason}>{item.performanceRank === 'champion' ? 'Referência elegível' : 'Em observação'}</td>
  </tr>)}</tbody></table></div>;
}

export function GoogleCampaignResults({ campaign, intelligence, loading }: {
  campaign: Campaign; intelligence?: GoogleIntelligenceRecord | null; loading?: boolean;
}) {
  const report = intelligence?.report;
  return <section className="campaign-review-section">
    <h3>Google Ads · Acompanhamento</h3>
    <p>{campaign.google_campaign_id ? 'Campanha vinculada à origem.' : 'Publique na etapa 3 para criar o vínculo com o Google Ads.'}</p>
    <dl><dt>Conta Google Ads</dt><dd>{campaign.google_customer_id || 'Não selecionada'}</dd>
      <dt>Campanha Google Ads</dt><dd>{campaign.google_campaign_id || 'Não publicada'}</dd>
      <dt>Sincronização</dt><dd>{campaign.google_sync_status || 'Sem sincronização'}</dd></dl>
    {campaign.google_last_error && <p role="alert">{campaign.google_last_error}</p>}
    {loading && <p>Carregando resultados Google Ads...</p>}
    {intelligence?.error && <p role="alert" className="campaign-channel-error">Falha na coleta Google Ads: {intelligence.error}</p>}
    {!loading && !report && !intelligence?.error && campaign.google_campaign_id && <p>Use “Re-analisar e Retroalimentar” para coletar os resultados Google Ads.</p>}
    {report && <>
      <p>Últimos 30 dias · Coletado em {new Date(report.collectedAt).toLocaleString('pt-BR')}</p>
      <dl className="google-performance-totals">
        <div><dt>Impressões</dt><dd>{number(report.totals.impressions)}</dd></div>
        <div><dt>Cliques</dt><dd>{number(report.totals.clicks)}</dd></div>
        <div><dt>Conversões</dt><dd>{number(report.totals.conversions)}</dd></div>
        <div><dt>CTR</dt><dd>{number(report.totals.ctrPercent)}%</dd></div>
        <div><dt>Custo</dt><dd>{money(report.totals.costMicros, report.currency)}</dd></div>
        <div><dt>Custo por conversão</dt><dd>{money(report.totals.cpaMicros, report.currency)}</dd></div>
      </dl>
      <PerformanceTable items={report.ads} label="Anúncios de Pesquisa" currency={report.currency}/>
      <PerformanceTable items={report.keywords} label="Palavras-chave" currency={report.currency}/>
      <small>Detalhamento dos {report.detailLimit} anúncios e palavras-chave com mais cliques. Os totais abrangem a campanha.</small>
    </>}
    <a className="campaign-journey-button" href="https://ads.google.com/" target="_blank" rel="noopener noreferrer">Abrir Google Ads</a>
  </section>;
}
